// Who is waiting, and letting them in. §25f.
//
// Behind the **same door** as the tuner and the rollout board: one audience,
// one password, one check (`TunerGate`). The server checks it again on both
// endpoints — this page is the visible half of the gate and not the gate.
//
// It opens on whoever is *pending*, because "who is waiting" is the question
// somebody opens this to answer, and typing an address you do not know is not
// a way to ask it. Searching is for the other question: what happened to this
// particular person who says they signed up.

import { createMongoAbility } from "@casl/ability";
import { useCallback, useEffect, useMemo, useState } from "react";

import { isRole, ROLE_DETAIL, ROLES, type Ability, type Role } from "src/engine/rbac/ability";
import { AdminBar, AdminNav, type AdminPage } from "./AdminNav";
import {
  Btn,
  Field,
  HoverCard,
  Notice,
  Page,
  PageColumn,
  Pill,
  Select,
  styled,
  Table,
  TableScroll,
  Text
} from "./kit";

interface Listed {
  id: number;
  email: string;
  approved: boolean;
  role: Role;
  /** From ADMIN_EMAILS rather than the database, so the screen cannot change it. */
  root: boolean;
  createdAt: string;
  approvedBy: string | null;
  roleSetBy: string | null;
}

/**
 * What *this* person may do, from the server's own rules.
 *
 * **Not a second opinion.** `/api/session` sends the rules `abilityFor` built,
 * so a control this screen draws is a control the server will accept — which is
 * the whole reason for the package. It is still enforced there: this decides
 * what to *render*, never what is allowed.
 */
function useAbility(): Ability | null {
  const [rules, setRules] = useState<unknown[] | null>(null);
  useEffect(() => {
    let live = true;
    fetch("/api/session", { credentials: "same-origin" })
      .then((response) => response.json())
      .then((session: { rules?: unknown[] }) => live && setRules(session.rules ?? []))
      .catch(() => live && setRules([]));
    return () => {
      live = false;
    };
  }, []);
  return useMemo(
    () => (rules === null ? null : (createMongoAbility(rules as never) as Ability)),
    [rules]
  );
}

export function UsersPage({
  onClose,
  onGo
}: {
  onClose: () => void;
  onGo: (page: AdminPage) => void;
}): React.ReactElement | null {
  // **No `TunerGated`.** That door is a shared password, which answers "does
  // this browser know a secret" and cannot answer "who is this" — so an
  // approval was recorded as "dashboard" and taking somebody's access away
  // meant changing a password everybody used. The account's own role decides
  // now, and the server checks it again on every endpoint.
  return <Users onClose={onClose} onGo={onGo} />;
}

const Screen = styled(Page)(({ theme }) => ({
  position: "fixed",
  inset: 0,
  zIndex: theme.kit.z.chrome
}));

const Spacer = styled("div")({ flex: 1 });

/** The search row: a field that takes the width, and the queue beside it. */
const Search = styled("div")(({ theme }) => ({
  display: "flex",
  alignItems: "center",
  gap: theme.kit.space.md,
  margin: `${theme.kit.space.lg}px 0`
}));

const Wide = styled("div")({ flex: "1 1 22rem", maxWidth: "32rem" });

/** The queue count, stated inline rather than as a block across the page. */
const Waiting = styled(Notice)({ display: "inline-block", padding: "6px 12px" });

/** One row's second line — who asked when, and who let them in. */
const Provenance = styled(Text)({ display: "block" });

/** The role list, for the card that explains the picker. */
const Roles = styled("div")(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  gap: theme.kit.space.xs
}));

function RoleCard(): React.ReactElement {
  return (
    <Roles>
      {ROLES.map((role) => (
        <Text key={role} block size={12}>
          <Text size={12} weight={700}>{role}</Text> —{" "}
          <Text size={12} tone="dim">{ROLE_DETAIL[role]}</Text>
        </Text>
      ))}
    </Roles>
  );
}

function Users({
  onClose,
  onGo
}: {
  onClose: () => void;
  onGo: (page: AdminPage) => void;
}): React.ReactElement {
  const [query, setQuery] = useState("");
  const [users, setUsers] = useState<Listed[] | null>(null);
  const [pending, setPending] = useState(0);
  const [failed, setFailed] = useState<string | null>(null);
  const [busy, setBusy] = useState<number | null>(null);
  const ability = useAbility();

  const load = useCallback((term: string) => {
    void fetch(`/api/users?q=${encodeURIComponent(term)}`, { credentials: "same-origin" })
      .then(async (response) => {
        if (!response.ok) throw new Error(response.status === 503 ? "no database" : "refused");
        return response.json() as Promise<{ users: Listed[]; pending: number }>;
      })
      .then((body) => {
        setUsers(body.users);
        setPending(body.pending);
        setFailed(null);
      })
      .catch((error: Error) => setFailed(error.message));
  }, []);

  // **Debounced, because this types into a database.** A query per keystroke on
  // a LIKE over every address is the one way this page could cost anything.
  useEffect(() => {
    const timer = setTimeout(() => load(query), 250);
    return () => clearTimeout(timer);
  }, [query, load]);

  const decide = (user: Listed, approved: boolean): void => {
    setBusy(user.id);
    void fetch("/api/users/approve", {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: user.id, approved })
    })
      .then((response) => {
        if (!response.ok) throw new Error("refused");
        load(query);
      })
      .catch((error: Error) => setFailed(error.message))
      .finally(() => setBusy(null));
  };

  if (ability !== null && !ability.can("read", "User")) {
    return (
      <Screen>
        <PageColumn width={640}>
          <Notice data-testid="users-forbidden">
            This screen is for approvers. If you should have access, ask an owner to give you the
            role — it takes effect the moment they do.
          </Notice>
          <Btn variant="ghost" onClick={onClose} style={{ marginTop: 16 }}>
            ← Back to the game
          </Btn>
        </PageColumn>
      </Screen>
    );
  }

  const setRoleOf = (user: Listed, role: Role): void => {
    setBusy(user.id);
    void fetch("/api/users/role", {
      method: "POST",
      headers: { "content-type": "application/json" },
      credentials: "same-origin",
      body: JSON.stringify({ id: user.id, role })
    })
      .then(async (response) => {
        if (response.ok) return load(query);
        const { error } = (await response.json().catch(() => ({}))) as { error?: string };
        throw new Error(
          error === "root"
            ? "That account is an owner through ADMIN_EMAILS, so this screen cannot change it."
            : error === "self"
              ? "You cannot change your own role — the last owner demoting themselves locks everybody out."
              : "refused"
        );
      })
      .catch((error: Error) => setFailed(error.message))
      .finally(() => setBusy(null));
  };

  return (
    <Screen>
      <AdminBar>
        <AdminNav current="users" onGo={onGo} />
        <Spacer />
        <Btn variant="ghost" small onClick={onClose}>
          Close
        </Btn>
      </AdminBar>
      <PageColumn width={980}>
        <Search>
          <Wide>
            <Field
              data-testid="users-search"
              placeholder="Search by email address"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </Wide>
          <Waiting data-testid="users-pending" tone={pending > 0 ? "warn" : "info"}>
            <Text role="display" weight={700}>
              {pending}
            </Text>{" "}
            <Text tone="dim">waiting</Text>
          </Waiting>
        </Search>

        {failed && (
          <Notice data-testid="users-error" tone="bad" style={{ marginBottom: 16 }}>
            {failed === "no database"
              ? "No database is configured on this deployment, so there are no accounts to show."
              : "That was refused. The dashboard cookie may have expired — reload and unlock again."}
          </Notice>
        )}

        {users === null && !failed && (
          <Text block tone="dim">
            Loading…
          </Text>
        )}

        {users?.length === 0 && (
          <Notice data-testid="users-empty">
            {query
              ? "Nobody with an address like that."
              : "Nobody is waiting. Everyone who has asked has been let in."}
          </Notice>
        )}

        {users !== null && users.length > 0 && (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <th>person</th>
                  <th>role</th>
                  <th>state</th>
                  <th />
                </tr>
              </thead>
              <tbody>
                {users.map((user) => (
                  <tr key={user.id} data-testid="users-row">
                    <td>
                      <Text block weight={700} style={{ overflowWrap: "anywhere" }}>
                        {user.email}
                      </Text>
                      {/* Who let somebody in is a question that gets asked, so
                          it is recorded and shown rather than only stored. */}
                      <Provenance tone="dim" size={12}>
                        asked {new Date(user.createdAt).toLocaleDateString()}
                        {user.approved && user.approvedBy ? ` · approved by ${user.approvedBy}` : ""}
                        {user.roleSetBy ? ` · role by ${user.roleSetBy}` : ""}
                      </Provenance>
                    </td>
                    <td>
                      {ability?.can("setRole", "User") && !user.root ? (
                        <HoverCard placement="top" title="Roles" card={<RoleCard />} maxWidth={320}>
                          <Select
                            data-testid="users-role"
                            value={user.role}
                            onChange={(event) => {
                              const role = event.target.value;
                              if (isRole(role)) setRoleOf(user, role);
                            }}
                          >
                            {ROLES.map((role) => (
                              <option key={role} value={role}>
                                {role}
                              </option>
                            ))}
                          </Select>
                        </HoverCard>
                      ) : (
                        <HoverCard
                          placement="top"
                          title={user.role}
                          card={
                            user.root
                              ? "From ADMIN_EMAILS, so this screen cannot change it."
                              : ROLE_DETAIL[user.role]
                          }
                        >
                          <Pill>
                            <Text size={12} weight={700}>
                              {user.root ? `${user.role} · root` : user.role}
                            </Text>
                          </Pill>
                        </HoverCard>
                      )}
                    </td>
                    <td>
                      <Pill>
                        <Text tone={user.approved ? "brand" : "dim"} size={12} weight={700}>
                          {user.approved ? "in" : "waiting"}
                        </Text>
                      </Pill>
                    </td>
                    <td>
                      {/* Drawn only if it will work. A control somebody cannot
                          use is a control that lies about what they may do. */}
                      {ability?.can("approve", "User") && (
                        <Btn
                          small
                          variant={user.approved ? "ghost" : "primary"}
                          data-testid={user.approved ? "users-revoke" : "users-approve"}
                          disabled={busy === user.id}
                          onClick={() => decide(user, !user.approved)}
                        >
                          {user.approved ? "Revoke" : "Approve"}
                        </Btn>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}
      </PageColumn>
    </Screen>
  );
}
